import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { OfertasService } from '../ofertas/services/OfertasService';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { APP_ROUTES } from '../../core/constants/routes.const';
import { OfertaDtoOut } from '../ofertas/models/OfertaDtoOut.model';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ChartModule } from 'primeng/chart';
import { CardModule } from 'primeng/card';
import { TARIFAS } from '../../core/enums/tarifas.enum';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    SkeletonModule,
    ToastModule,
    ChartModule,
    DatePipe,
    TooltipModule,
    CardModule,
  ],
  providers: [MessageService],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  private readonly ofertasApi = inject(OfertasService);
  private readonly router = inject(Router);
  private readonly messages = inject(MessageService);

  readonly R = APP_ROUTES;
  readonly T = TARIFAS;

  loading = signal(true);
  ofertas = signal<OfertaDtoOut[]>([]);

  chartData = computed(() => {
    const data = this.ofertas();

    const months = Array.from({ length: 6 }).map((_, i) => {
      const d = new Date();
      d.setMonth(d.getMonth() - (5 - i));
      return d;
    });

    const labels = months.map((m) => m.toLocaleDateString('es-ES', { month: 'short' }));

    const dataset20 = months.map(
      (m) =>
        data.filter(
          (o) =>
            o.tipoTarifa === TARIFAS.TD20 &&
            o.createdAt &&
            new Date(o.createdAt).getMonth() === m.getMonth() &&
            new Date(o.createdAt).getFullYear() === m.getFullYear()
        ).length
    );

    const dataset30 = months.map(
      (m) =>
        data.filter(
          (o) =>
            o.tipoTarifa === TARIFAS.TD30 &&
            o.createdAt &&
            new Date(o.createdAt).getMonth() === m.getMonth() &&
            new Date(o.createdAt).getFullYear() === m.getFullYear()
        ).length
    );

    return {
      labels,
      datasets: [
        {
          label: TARIFAS.TD20,
          backgroundColor: '#3B82F6',
          data: dataset20,
        },
        {
          label: TARIFAS.TD30,
          backgroundColor: '#F59E0B',
          data: dataset30,
        },
      ],
    };
  });

  chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
    },
    scales: {
      y: { beginAtZero: true },
    },
  };

  // MOCK
  resumen = [
    { title: 'Total ofertas', value: 120, icon: 'pi pi-briefcase', color: 'text-blue-500' },
    { title: 'Este mes', value: 18, icon: 'pi pi-calendar', color: 'text-green-500' },
    { title: 'Aceptadas', value: 45, icon: 'pi pi-check-circle', color: 'text-emerald-500' },
    { title: 'Rechazadas', value: 12, icon: 'pi pi-times-circle', color: 'text-red-500' },
  ];

  ngOnInit(): void {
    this.cargar();
  }

  cargar() {
    this.loading.set(true);
    this.ofertasApi.getMisOfertas().subscribe({
      next: (data) => this.ofertas.set(data ?? []),
      error: () => {
        this.messages.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar tus ofertas',
        });
      },
      complete: () => this.loading.set(false),
    });
  }

  nuevaOferta() {
    this.router.navigate([`/${this.R.OFERTAS.NUEVA}`]).then();
  }

  verDetalle(oferta: OfertaDtoOut) {
    this.router.navigate([`/${this.R.OFERTAS.DETALLE(oferta.id)}`]).then();
  }
}
